<template>
  <div class="intellect-device-overview" v-loading="pageLoading" element-loading-text="数据加载中...">
    <!-- 顶部筛选栏 -->
    <el-card class="filter-bar" shadow="never">
      <div class="filter-content">
        <h2 class="page-title">智能看护设备总览</h2>
        <el-date-picker
          v-model="filters.month"
          type="month"
          placeholder="选择月份"
          value-format="yyyy-MM"
          @change="handleFilterChange"
        />
      </div>
    </el-card>

    <!-- 本月汇总快览 -->
    <div class="summary-section">
      <el-card class="stat-card">
        <div class="stat-title">总上报次数</div>
        <div class="stat-value">
          {{ formatNumber(summary.totalReports || 0) }}
          <span class="stat-unit">次</span>
        </div>
      </el-card>
      <el-card class="stat-card">
        <div class="stat-title">有效报警率</div>
        <div class="stat-value">
          {{ summary.validAlertRate || 0 }}
          <span class="stat-unit">%</span>
        </div>
      </el-card>
      <el-card class="stat-card">
        <div class="stat-title">已处置报警数</div>
        <div class="stat-value">
          {{ formatNumber(summary.handledAlerts || 0) }}
          <span class="stat-unit">个</span>
        </div>
      </el-card>
    </div>

    <!-- 核心指标卡片 -->
    <div class="core-stats-section">
      <el-card class="metric-card">
        <div class="metric-title">设备总数</div>
        <div class="metric-content">
          <span class="metric-value">{{ coreStats.totalDevices || 0 }}</span>
          <span class="metric-unit">台</span>
          <span v-if="coreStats.totalDevicesTrend" :class="getTrendClass(coreStats.totalDevicesTrend)">
            {{ getTrendText(coreStats.totalDevicesTrend) }}
          </span>
        </div>
      </el-card>

      <el-card class="metric-card">
        <div class="metric-title">在线设备</div>
        <div class="metric-content">
          <span class="metric-value">{{ coreStats.onlineDevices || 0 }}</span>
          <span class="metric-unit">台</span>
          <span v-if="coreStats.onlineDevicesTrend" :class="getTrendClass(coreStats.onlineDevicesTrend)">
            {{ getTrendText(coreStats.onlineDevicesTrend) }}
          </span>
        </div>
      </el-card>

      <el-card class="metric-card highlight">
        <div class="metric-title">设备在线率</div>
        <div class="metric-content">
          <span class="metric-value">{{ coreStats.onlineRate || 0 }}</span>
          <span class="metric-unit">%</span>
          <span v-if="coreStats.onlineRateTrend" :class="getTrendClass(coreStats.onlineRateTrend)">
            {{ getTrendText(coreStats.onlineRateTrend) }}%
          </span>
        </div>
      </el-card>

      <el-card class="metric-card">
        <div class="metric-title">本月报警总数</div>
        <div class="metric-content">
          <span class="metric-value">{{ coreStats.totalAlarms || 0 }}</span>
          <span class="metric-unit">次</span>
          <span v-if="coreStats.totalAlarmsTrend" :class="getTrendClass(coreStats.totalAlarmsTrend)">
            {{ getTrendText(coreStats.totalAlarmsTrend) }}%
          </span>
        </div>
      </el-card>

      <el-card class="metric-card">
        <div class="metric-title">报警处置率</div>
        <div class="metric-content">
          <span class="metric-value">{{ coreStats.handleRate || 0 }}</span>
          <span class="metric-unit">%</span>
          <span v-if="coreStats.handleRateTrend" :class="getTrendClass(coreStats.handleRateTrend)">
            {{ getTrendText(coreStats.handleRateTrend) }}%
          </span>
        </div>
      </el-card>

      <el-card class="metric-card warning">
        <div class="metric-title">未处置报警</div>
        <div class="metric-content">
          <span class="metric-value">{{ coreStats.pendingAlarms || 0 }}</span>
          <span class="metric-unit">条</span>
        </div>
      </el-card>
    </div>

    <!-- 设备报警明细 -->
    <div class="device-alarm-section">
      <el-card 
        v-for="device in deviceAlarmBrief" 
        :key="device.deviceTypeCode"
        class="device-card"
      >
        <div class="device-info">
          <div class="device-name">{{ device.deviceType }}</div>
          <div class="device-stats">
            <div class="stat-item">
              <span class="label">本月报警:</span>
              <span class="value">{{ device.alarmCount }}</span>
            </div>
            <div class="stat-item">
              <span class="label">环比:</span>
              <span :class="getTrendClass(device.monthOnMonth)">
                {{ device.monthOnMonth > 0 ? '+' : '' }}{{ device.monthOnMonth }}%
              </span>
            </div>
            <div class="stat-item">
              <span class="label">有效:</span>
              <span class="value">{{ device.validAlarmCount }}</span>
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 图表区域 -->
    <div class="charts-section">
      <el-card class="chart-card chart-card-full">
        <div slot="header" class="chart-header">
          <span>{{ filters.month ? filters.month + ' 每日统计' : '每月统计概览' }}</span>
          <span class="chart-hint">可拖动下方滑块缩放</span>
        </div>
        <div ref="dailyChartRef" style="height: 360px"></div>
      </el-card>

      <el-card class="chart-card">
        <div slot="header" class="chart-header">
          <span>报警来源类型分布</span>
        </div>
        <div ref="pieChartRef" style="height: 400px"></div>
      </el-card>

      <el-card class="chart-card">
        <div slot="header" class="chart-header">
          <span>{{ filters.month ? filters.month + ' 各设备每日报警' : '各设备报警趋势' }}</span>
          <span class="chart-hint">可拖动下方滑块缩放</span>
        </div>
        <div ref="trendChartRef" style="height: 400px"></div>
      </el-card>
    </div>

    <!-- 底部区域 -->
    <div class="bottom-section">
      <el-card class="table-card">
        <div slot="header">报警最多老人 TOP10</div>
        <el-table :data="topElders" stripe>
          <el-table-column prop="rank" label="排名" width="80" />
          <el-table-column prop="oldmanName" label="姓名" />
          <el-table-column prop="room" label="位置" />
          <el-table-column prop="mainDeviceType" label="设备" />
          <el-table-column prop="alarmCount" label="报警次数" />
          <el-table-column prop="lastAlarmTime" label="最近报警" />
        </el-table>
      </el-card>

      <el-card class="alarm-list-card">
        <div slot="header" class="alarm-list-header">
          <span>未处置报警明细</span>
          <el-tag size="mini" type="danger" v-if="pendingAlarms.length">{{ pendingAlarms.length }}条</el-tag>
        </div>
        <div class="alarm-list">
          <div 
            v-for="(item, index) in pendingAlarms" 
            :key="index"
            :class="['alarm-item', getWaitingLevel(item.waitingMinutes)]"
          >
            <div class="alarm-item-left">
              <div class="alarm-time">{{ item.alertTime }}</div>
              <div class="alarm-info">
                <el-tag size="mini" type="primary" effect="plain">{{ item.deviceType }}</el-tag>
                <span class="oldman-name">{{ item.oldmanName }}</span>
                <span class="room">{{ item.room }}</span>
              </div>
              <div class="alarm-type">{{ item.alertType }}</div>
            </div>
            <div class="alarm-item-right">
              <div :class="getWaitingClass(item.waitingMinutes)">
                等待 {{ item.waitingMinutes }} 分钟
              </div>
            </div>
          </div>
          <div v-if="!pendingAlarms.length" class="alarm-empty">
            <i class="el-icon-check" style="font-size:32px;color:#67C23A"></i>
            <div>暂无未处置报警</div>
          </div>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import echarts from 'echarts'
import {
  getOverviewSummary,
  getCoreStats,
  getDeviceAlarmBrief,
  getMonthlyStats,
  getDailyStats,
  getAlarmSourcePie,
  getAlarmTrend,
  getDailyAlarmTrend,
  getTopAlarmElders,
  getPendingAlarms
} from '@/api/dmc/intellect-overview'

export default {
  name: 'IntellectDeviceOverview',
  data() {
    return {
      filters: {
        month: null
      },
      summary: {},
      coreStats: {},
      deviceAlarmBrief: [],
      monthlyStatsData: {},
      alarmSourceData: {},
      alarmTrendData: {},
      topElders: [],
      pendingAlarms: [],
      pageLoading: false,
      dailyChart: null,
      pieChart: null,
      trendChart: null
    }
  },
  mounted() {
    this.loadAllData()
    this._resizeHandler = () => {
      if (this.dailyChart) this.dailyChart.resize()
      if (this.pieChart) this.pieChart.resize()
      if (this.trendChart) this.trendChart.resize()
    }
    window.addEventListener('resize', this._resizeHandler)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._resizeHandler)
    if (this.dailyChart) this.dailyChart.dispose()
    if (this.pieChart) this.pieChart.dispose()
    if (this.trendChart) this.trendChart.dispose()
  },
  methods: {
    async loadAllData() {
      this.pageLoading = true
      try {
        const params = {
          month: this.filters.month || undefined
        }

        const hasMonth = !!this.filters.month
        const results = await Promise.all([
          getOverviewSummary(params),
          getCoreStats(params),
          getDeviceAlarmBrief(params),
          hasMonth ? getDailyStats(params) : getMonthlyStats(params),
          getAlarmSourcePie(params),
          hasMonth ? getDailyAlarmTrend(params) : getAlarmTrend({ ...params, months: 12 }),
          getTopAlarmElders({ ...params, top: 10 }),
          getPendingAlarms({ ...params, pageNum: 1, pageSize: 20 })
        ])

        this.summary = results[0] || {}
        this.coreStats = results[1] || {}
        this.deviceAlarmBrief = results[2] || []
        this.monthlyStatsData = results[3] || {}
        this.alarmSourceData = results[4] || {}
        this.alarmTrendData = results[5] || {}
        this.topElders = results[6] || []
        this.pendingAlarms = (results[7] && results[7].list) || []

        this.$nextTick(() => {
          this.renderCharts()
        })
      } catch (error) {
        console.error('加载数据失败:', error)
        this.$message.error('加载数据失败，请稍后重试')
      } finally {
        this.pageLoading = false
      }
    },
    renderCharts() {
      this.renderDailyChart()
      this.renderPieChart()
      this.renderTrendChart()
    },
    renderDailyChart() {
      if (!this.$refs.dailyChartRef) return
      
      if (!this.dailyChart) {
        this.dailyChart = echarts.init(this.$refs.dailyChartRef)
      }

      const months = this.monthlyStatsData.months || []
      const alarmCounts = this.monthlyStatsData.alarmCounts || []
      const deviceLogCounts = this.monthlyStatsData.deviceLogCounts || []
      const healthCheckCounts = this.monthlyStatsData.healthCheckCounts || []

      const option = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(50,50,50,0.9)',
          textStyle: { color: '#fff' },
          axisPointer: { type: 'shadow' }
        },
        legend: {
          data: ['报警次数', '设备日志次数', '健康监测次数'],
          top: 4,
          textStyle: { fontSize: 13, color: '#606266' }
        },
        grid: { left: 60, right: 30, top: 50, bottom: 70 },
        dataZoom: [
          { type: 'slider', start: 0, end: 100, height: 20, bottom: 10 },
          { type: 'inside' }
        ],
        xAxis: {
          type: 'category',
          data: months,
          axisLabel: { fontSize: 12, color: '#606266' },
          axisTick: { alignWithLabel: true }
        },
        yAxis: {
          type: 'value',
          name: '次数',
          splitLine: { lineStyle: { type: 'dashed', color: '#E4E7ED' } }
        },
        series: [
          {
            name: '报警次数',
            type: 'bar',
            data: alarmCounts,
            barMaxWidth: 28,
            itemStyle: {
              borderRadius: [4, 4, 0, 0],
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#F56C6C' },
                { offset: 1, color: '#fab6b6' }
              ])
            }
          },
          {
            name: '设备日志次数',
            type: 'bar',
            data: deviceLogCounts,
            barMaxWidth: 28,
            itemStyle: {
              borderRadius: [4, 4, 0, 0],
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#409EFF' },
                { offset: 1, color: '#79bbff' }
              ])
            }
          },
          {
            name: '健康监测次数',
            type: 'bar',
            data: healthCheckCounts,
            barMaxWidth: 28,
            itemStyle: {
              borderRadius: [4, 4, 0, 0],
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#67C23A' },
                { offset: 1, color: '#b3e19d' }
              ])
            }
          }
        ]
      }
      this.dailyChart.setOption(option)
    },
    renderPieChart() {
      if (!this.$refs.pieChartRef) return
      
      if (!this.pieChart) {
        this.pieChart = echarts.init(this.$refs.pieChartRef)
      }

      const distribution = this.alarmSourceData.distribution || []
      const colorPalette = [
        '#5470c6','#91cc75','#fac858','#ee6666','#73c0de',
        '#3ba272','#fc8452','#9a60b4','#ea7ccc','#409EFF',
        '#67C23A','#E6A23C','#F56C6C','#909399','#00b4d8'
      ]
      const option = {
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(50,50,50,0.9)',
          textStyle: { color: '#fff' },
          formatter: function(p) {
            return '<b>' + p.name + '</b><br/>' + '报警数: ' + p.value + ' (' + p.percent + '%)'
          }
        },
        legend: {
          type: 'scroll',
          orient: 'vertical',
          right: 10,
          top: 20,
          bottom: 20,
          textStyle: { fontSize: 12, color: '#606266' },
          pageIconColor: '#409EFF',
          pageTextStyle: { color: '#606266' }
        },
        color: colorPalette,
        series: [{
          type: 'pie',
          radius: ['35%', '65%'],
          center: ['40%', '50%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 6,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: true,
            formatter: '{b}: {d}%',
            fontSize: 11,
            color: '#606266'
          },
          labelLine: {
            length: 12,
            length2: 16
          },
          emphasis: {
            label: { show: true, fontSize: 14, fontWeight: 'bold' },
            itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.2)' }
          },
          data: distribution.map(item => ({
            name: item.deviceType,
            value: item.alarmCount
          }))
        }]
      }
      this.pieChart.setOption(option)
    },
    renderTrendChart() {
      if (!this.$refs.trendChartRef) return
      
      if (!this.trendChart) {
        this.trendChart = echarts.init(this.$refs.trendChartRef)
      }

      const months = this.alarmTrendData.months || []
      const series = this.alarmTrendData.series || []
      const colorPalette = [
        '#5470c6','#91cc75','#fac858','#ee6666','#73c0de',
        '#3ba272','#fc8452','#9a60b4','#ea7ccc','#409EFF',
        '#67C23A','#E6A23C','#F56C6C','#909399','#00b4d8'
      ]
      const option = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(50,50,50,0.9)',
          textStyle: { color: '#fff' },
          confine: true
        },
        legend: {
          type: 'scroll',
          top: 0,
          left: 'center',
          textStyle: { fontSize: 11, color: '#606266' },
          pageIconColor: '#409EFF',
          pageTextStyle: { color: '#606266' },
          data: series.map(item => item.deviceType)
        },
        grid: { left: 50, right: 30, top: 50, bottom: 70 },
        dataZoom: [
          { type: 'slider', start: 0, end: 100, height: 20, bottom: 10 },
          { type: 'inside' }
        ],
        color: colorPalette,
        xAxis: {
          type: 'category',
          data: months,
          boundaryGap: false,
          axisLabel: { fontSize: 12, color: '#606266' }
        },
        yAxis: {
          type: 'value',
          name: '报警次数',
          splitLine: { lineStyle: { type: 'dashed', color: '#E4E7ED' } }
        },
        series: series.map(item => ({
          name: item.deviceType,
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { width: 2 },
          data: item.data
        }))
      }
      this.trendChart.setOption(option)
    },
    formatNumber(num) {
      return num.toLocaleString()
    },
    getTrendClass(trend) {
      if (trend > 0) return 'trend-up'
      if (trend < 0) return 'trend-down'
      return ''
    },
    getTrendText(trend) {
      const abs = Math.abs(trend)
      return trend > 0 ? `+${abs}` : `-${abs}`
    },
    getWaitingClass(minutes) {
      if (minutes > 120) return 'waiting-critical'
      if (minutes > 60) return 'waiting-warning'
      return 'waiting-normal'
    },
    getWaitingLevel(minutes) {
      if (minutes > 120) return 'alarm-level-critical'
      if (minutes > 60) return 'alarm-level-warning'
      return 'alarm-level-normal'
    },
    handleFilterChange() {
      this.loadAllData()
    }
  }
}
</script>

<style scoped>
.intellect-device-overview {
  padding: 20px;
  background: #f2f3f5;
}

.filter-bar {
  margin-bottom: 20px;
}

.filter-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.summary-section {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.stat-card {
  flex: 1;
  text-align: center;
}

.stat-title {
  font-size: 14px;
  color: #909399;
  margin-bottom: 12px;
}

.stat-value {
  font-size: 36px;
  font-weight: 600;
  color: #303133;
}

.stat-unit {
  font-size: 16px;
  margin-left: 4px;
}

.core-stats-section {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.metric-card {
  text-align: center;
}

.metric-title {
  font-size: 14px;
  color: #909399;
  margin-bottom: 8px;
}

.metric-content {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
}

.metric-value {
  font-size: 28px;
  font-weight: 600;
  color: #303133;
}

.metric-unit {
  font-size: 14px;
  color: #909399;
}

.trend-up {
  color: #F56C6C;
  font-size: 12px;
}

.trend-down {
  color: #67C23A;
  font-size: 12px;
}

.metric-card.highlight {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
}

.metric-card.highlight .metric-title,
.metric-card.highlight .metric-value,
.metric-card.highlight .metric-unit {
  color: #fff;
}

.metric-card.warning {
  background: #fef0f0;
  border: 1px solid #fbc4c4;
}

.metric-card.warning .metric-value {
  color: #F56C6C;
}

.device-alarm-section {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.device-card .device-info {
  padding: 10px 0;
}

.device-name {
  font-size: 16px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 12px;
}

.device-stats .stat-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
}

.device-stats .label {
  color: #909399;
}

.device-stats .value {
  color: #303133;
  font-weight: 500;
}

/* ---- 图表区域 ---- */
.charts-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.chart-card-full {
  grid-column: 1 / -1;
}

.chart-card {
  min-height: 440px;
  border-radius: 8px;
  overflow: hidden;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.chart-hint {
  font-size: 12px;
  font-weight: 400;
  color: #C0C4CC;
}

/* ---- 底部区域 ---- */
.bottom-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.table-card,
.alarm-list-card {
  border-radius: 8px;
  overflow: hidden;
}

/* ---- 报警列表头部 ---- */
.alarm-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

/* ---- 报警列表 ---- */
.alarm-list {
  max-height: 520px;
  overflow-y: auto;
  padding-right: 4px;
}

.alarm-list::-webkit-scrollbar {
  width: 6px;
}

.alarm-list::-webkit-scrollbar-thumb {
  background: #C0C4CC;
  border-radius: 3px;
}

.alarm-list::-webkit-scrollbar-track {
  background: transparent;
}

.alarm-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 14px 12px;
  margin-bottom: 8px;
  background: #FAFAFA;
  border-radius: 6px;
  border-left: 4px solid #C0C4CC;
  transition: background 0.2s, box-shadow 0.2s;
}

.alarm-item:hover {
  background: #F0F2F5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.alarm-item.alarm-level-critical {
  border-left-color: #F56C6C;
  background: #FEF0F0;
}

.alarm-item.alarm-level-critical:hover {
  background: #FDE2E2;
}

.alarm-item.alarm-level-warning {
  border-left-color: #E6A23C;
  background: #FDF6EC;
}

.alarm-item.alarm-level-warning:hover {
  background: #FAECD8;
}

.alarm-item.alarm-level-normal {
  border-left-color: #409EFF;
  background: #F0F9FF;
}

.alarm-item.alarm-level-normal:hover {
  background: #E1F3FF;
}

.alarm-item-left {
  flex: 1;
  min-width: 0;
}

.alarm-item-right {
  flex-shrink: 0;
  margin-left: 12px;
  text-align: right;
}

.alarm-time {
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}

.alarm-info {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.oldman-name {
  font-weight: 600;
  font-size: 14px;
  color: #303133;
}

.room {
  font-size: 13px;
  color: #909399;
}

.alarm-type {
  font-size: 13px;
  color: #606266;
  line-height: 1.5;
  word-break: break-all;
}

.waiting-critical {
  color: #F56C6C;
  font-size: 14px;
  font-weight: 700;
}

.waiting-warning {
  color: #E6A23C;
  font-size: 14px;
  font-weight: 600;
}

.waiting-normal {
  color: #909399;
  font-size: 13px;
}

.alarm-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: #909399;
  font-size: 14px;
  gap: 12px;
}
</style>
